import React, { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import { withStyles } from '@mui/styles'
import {
  Box,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { expansionPanelStyles } from './expansion-panel.styles'

/**
 * @typedef {object} ExpansionPanelComponentProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {'left' | 'right'} [triggerPosition='left'] - Prop recibida pero no utilizada actualmente en la lógica del componente.
 * @property {boolean} [isExpanded=false] - Estado inicial del panel (expandido o colapsado).
 * @property {React.ReactNode} summary - Contenido que se muestra en el resumen del panel (siempre visible).
 * @property {React.ReactNode} [summaryExtra] - Contenido adicional que se muestra en el resumen, alineado a la derecha.
 * @property {React.ReactNode} details - Contenido detallado que se muestra cuando el panel está expandido.
 * @property {React.ReactNode} [footer] - Contenido que se muestra debajo del panel, fuera del accordion.
 */

/**
 * Un componente de panel de expansión con resumen y detalles desplegables.
 *
 * Proporciona un accordion mejorado con áreas para resumen, contenido extra en el resumen,
 * detalles expandibles con separador visual, y un footer opcional. El panel incluye
 * animaciones suaves al expandir/colapsar y gestiona su propio estado de expansión.
 *
 * @component
 * @example
 * const MyComponent = () => {
 *   return (
 *     <ExpansionPanelComponent
 *       isExpanded={true}
 *       summary={<Typography variant="h6">Resumen del Pedido</Typography>}
 *       summaryExtra={<Chip label="3 items" color="primary" />}
 *       details={
 *         <Box>
 *           <Typography>Producto 1: 100€</Typography>
 *           <Typography>Producto 2: 50€</Typography>
 *           <Typography>Producto 3: 25€</Typography>
 *         </Box>
 *       }
 *       footer={<Typography variant="caption">Total: 175€</Typography>}
 *     />
 *   );
 * };
 *
 * @param {ExpansionPanelComponentProps} props - Las props del componente ExpansionPanelComponent.
 */
export const ExpansionPanelComponent = withStyles(expansionPanelStyles)(({
  classes,
  triggerPosition = 'left',
  isExpanded = false,
  ...props
}) => {
  const { formatMessage } = useIntl()

  const [expanded, setExpanded] = useState(isExpanded)
  const handleToggleExpanded = useCallback((event, isExpanded) => {
    setExpanded(isExpanded)
  }, [])

  return (
    <>
      <Accordion className={classes.accordion} expanded={expanded} onChange={handleToggleExpanded}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ alignItems: 'center' }}>
          <Box display="flex" alignItems="center" width="100%">
            {props.summary}
            {props.summaryExtra && (
              <Box flexGrow={1} ml={2}>
                {props.summaryExtra}
              </Box>
            )}
          </Box>
        </AccordionSummary>
        {expanded && (
          <Box display="flex" justifyContent="center" width="100%">
            <Divider light style={{ width: '95%' }} />
          </Box>
        )}{' '}
        <AccordionDetails>{props.details}</AccordionDetails>
      </Accordion>
      {props.footer && (
        <Box ml={2} mr={2}>
          {props.footer}
        </Box>
      )}
    </>
  )
})
